import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Loader2,
  Pencil,
  ShieldCheck,
  User,
  Crown,
  ShieldMinus,
  AlertTriangle,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/format";

const inputClass =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-slate-50 disabled:text-slate-400";
const labelClass = "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500";

const ROLE_META = {
  super_admin: { label: "Главен администратор", Icon: Crown, badge: "bg-amber-100 text-amber-700", avatar: "bg-amber-100 text-amber-600" },
  admin: { label: "Администратор", Icon: ShieldCheck, badge: "bg-primary/10 text-primary", avatar: "bg-primary/10 text-primary" },
  editor: { label: "Редактор", Icon: User, badge: "bg-slate-100 text-slate-500", avatar: "bg-slate-100 text-slate-500" },
};
const meta = (r) => ROLE_META[r] || ROLE_META.editor;

function UserDialog({ open, onClose, user, onSaved, isSuper }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);
  const isEdit = !!user;

  useEffect(() => {
    if (user) {
      setForm({ email: user.email, name: user.name || "", password: "", role: user.role || "editor" });
    } else {
      setForm({ email: "", name: "", password: "", role: "editor" });
    }
  }, [user, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // super_admin role is only assigned via the email-confirmed flow, never this dropdown
  const roleLocked = isEdit && user?.role === "super_admin";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name };
        if (form.role !== "super_admin") payload.role = form.role;
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${user.id}`, payload);
        toast.success("Профилът е обновен.");
      } else {
        await api.post("/admin/users", form);
        toast.success("Потребителят е добавен.");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? "Редактиране на профил" : "Нов потребител"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Имейл</label>
            <input type="email" required disabled={isEdit} className={inputClass} data-testid="user-email" value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className={labelClass}>Име</label>
            <input className={inputClass} data-testid="user-name" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <label className={labelClass}>{isEdit ? "Нова парола (по избор)" : "Парола"}</label>
            <input type="password" required={!isEdit} className={inputClass} data-testid="user-password" value={form.password} onChange={set("password")} placeholder={isEdit ? "Оставете празно за без промяна" : "Поне 6 символа"} />
          </div>
          <div>
            <label className={labelClass}>Роля</label>
            {roleLocked ? (
              <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
                <Crown className="h-4 w-4" /> Главен администратор
              </div>
            ) : (
              <select className={inputClass} data-testid="user-role" value={form.role} onChange={set("role")}>
                <option value="editor">Редактор</option>
                {isSuper && <option value="admin">Администратор</option>}
              </select>
            )}
            {!isSuper && !roleLocked && (
              <p className="mt-1 text-xs text-slate-400">Само Главен администратор може да създава администратори.</p>
            )}
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отказ
            </button>
            <button type="submit" disabled={saving} data-testid="user-save-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Запази
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuperAdminDialog({ open, onClose, target, action, onRequested }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPassword(""); }, [open]);

  const grant = action === "grant";
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/super-admin/request", {
        target_user_id: target.id,
        action,
        password,
      });
      onRequested(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="super-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Crown className="h-5 w-5 text-amber-500" />
            {grant ? "Назначаване на Главен администратор" : "Сваляне на Главен администратор"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-600">
            {grant
              ? `Ще изпратим код за потвърждение по имейл, за да направите „${target?.name || target?.email}“ Главен администратор с пълни права.`
              : `Ще изпратим код за потвърждение по имейл, за да свалите правата на Главен администратор от „${target?.name || target?.email}“.`}
          </p>
          <div>
            <label className={labelClass}>Потвърдете с вашата парола</label>
            <input type="password" required className={inputClass} data-testid="super-admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отказ
            </button>
            <button type="submit" disabled={saving} data-testid="super-admin-request-btn" className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Изпрати код
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PurgeDialog({ open, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPassword(""); setConfirm(""); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users/purge", { password, confirm });
      toast.success(data.message);
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Премахване на всички потребители
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Това ще изтрие <b>всички други профили</b> (администратори и редактори), освен вашия. Действието е <b>необратимо</b>.
          </div>
          <div>
            <label className={labelClass}>Вашата парола</label>
            <input type="password" required className={inputClass} data-testid="purge-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div>
            <label className={labelClass}>Напишете ИЗТРИЙ за потвърждение</label>
            <input required className={inputClass} data-testid="purge-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="ИЗТРИЙ" />
          </div>
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отказ
            </button>
            <button type="submit" disabled={saving || confirm !== "ИЗТРИЙ"} data-testid="purge-submit-btn" className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-40">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Изтрий всички
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersManager() {
  const qc = useQueryClient();
  const { user: current, refresh } = useAuth();
  const isSuper = current?.role === "super_admin";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saDialog, setSaDialog] = useState(null); // { target, action }
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [pendingCode, setPendingCode] = useState("");
  const [fallbackCode, setFallbackCode] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get("/admin/users").then((r) => r.data),
  });

  const { data: pending } = useQuery({
    queryKey: ["super-admin-pending"],
    queryFn: () => api.get("/admin/super-admin/pending").then((r) => r.data),
    enabled: isSuper,
    refetchInterval: 15000,
  });

  const refreshUsers = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const refreshPending = () => qc.invalidateQueries({ queryKey: ["super-admin-pending"] });
  const superCount = (users || []).filter((u) => u.role === "super_admin").length;

  const del = async (u) => {
    if (!window.confirm(`Изтриване на профила на „${u.name || u.email}“?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Профилът е изтрит.");
      refreshUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onRequested = (res) => {
    refreshPending();
    if (!res.email_sent && res.code) {
      setFallbackCode(res.code);
      toast.warning("Имейлът не можа да се изпрати. Използвайте показания код по-долу.");
    } else {
      setFallbackCode(null);
      toast.success(`Код за потвърждение е изпратен на ${res.sent_to}.`);
    }
  };

  const confirmChange = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/admin/super-admin/confirm", { code: pendingCode.trim() });
      toast.success(data.message);
      setPendingCode("");
      setFallbackCode(null);
      refreshUsers();
      refreshPending();
      if (refresh) refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  const cancelChange = async () => {
    try {
      await api.post("/admin/super-admin/cancel");
    } catch (e) { /* ignore */ }
    setFallbackCode(null);
    setPendingCode("");
    refreshPending();
  };

  return (
    <div className="mt-6" data-testid="users-manager">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-slate-900">Потребители</h2>
        <div className="flex items-center gap-2">
          {isSuper && (users || []).length > 1 && (
            <button
              onClick={() => setPurgeOpen(true)}
              data-testid="purge-users-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            >
              <AlertTriangle className="h-4 w-4" />
              Премахни всички
            </button>
          )}
          <button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="add-user-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8]"
          >
            <Plus className="h-4 w-4" />
            Нов потребител
          </button>
        </div>
      </div>

      {/* Pending super-admin change banner */}
      {isSuper && pending?.pending && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="super-admin-pending">
          <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
            <Crown className="h-4 w-4" />
            {pending.action === "grant" ? "Назначаване на нов Главен администратор" : "Сваляне на Главен администратор"} — {pending.target_email}
          </div>
          <p className="mt-1 text-xs text-amber-700">
            Код за потвърждение е изпратен на <b>{pending.sent_to}</b>. Въведете го, за да завършите промяната.
          </p>
          {fallbackCode && (
            <div className="mt-2 rounded-lg bg-white px-3 py-2 text-sm text-slate-700 ring-1 ring-amber-200" data-testid="fallback-code">
              Имейлът не е доставен (подателят не е верифициран). Вашият код: <b className="font-mono tracking-widest">{fallbackCode}</b>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              className="w-40 rounded-lg border border-amber-300 px-3 py-2 text-center font-mono tracking-[0.3em] outline-none focus:ring-2 focus:ring-amber-300"
              data-testid="super-admin-confirm-code"
              value={pendingCode}
              onChange={(e) => setPendingCode(e.target.value)}
              placeholder="000000"
            />
            <button onClick={confirmChange} disabled={confirming || !pendingCode} data-testid="super-admin-confirm-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-50">
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
              Потвърди
            </button>
            <button onClick={cancelChange} data-testid="super-admin-cancel-btn" className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Отмени
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="divide-y divide-slate-100">
            {(users || []).map((u) => {
              const m = meta(u.role);
              const isMe = current?.email === u.email;
              const canEdit = isSuper || u.role === "editor" || isMe;
              const canDelete = !isMe && u.role !== "super_admin" && (isSuper || u.role === "editor");
              const canGrant = isSuper && u.role !== "super_admin";
              const canRevoke = isSuper && u.role === "super_admin" && superCount > 1;
              return (
                <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`user-row-${u.id}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-full ${m.avatar}`}>
                    <m.Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-sm font-bold text-slate-900">{u.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${m.badge}`}>{m.label}</span>
                      {isMe && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-primary">Вие</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {u.email} · от {formatDate(u.created_at)}
                    </div>
                  </div>

                  {canGrant && (
                    <button
                      onClick={() => setSaDialog({ target: u, action: "grant" })}
                      data-testid={`grant-super-${u.id}`}
                      className="rounded-lg p-2 text-amber-500 hover:bg-amber-50"
                      title="Направи Главен администратор"
                    >
                      <Crown className="h-4 w-4" />
                    </button>
                  )}
                  {canRevoke && (
                    <button
                      onClick={() => setSaDialog({ target: u, action: "revoke" })}
                      data-testid={`revoke-super-${u.id}`}
                      className="rounded-lg p-2 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                      title="Свали от Главен администратор"
                    >
                      <ShieldMinus className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => { setEditing(u); setDialogOpen(true); }}
                    disabled={!canEdit}
                    data-testid={`edit-user-${u.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-primary disabled:opacity-30"
                    aria-label="Редактирай"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => del(u)}
                    disabled={!canDelete}
                    data-testid={`delete-user-${u.id}`}
                    className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                    aria-label="Изтрий"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <UserDialog open={dialogOpen} onClose={() => setDialogOpen(false)} user={editing} onSaved={refreshUsers} isSuper={isSuper} />
      <SuperAdminDialog
        open={!!saDialog}
        onClose={() => setSaDialog(null)}
        target={saDialog?.target}
        action={saDialog?.action}
        onRequested={onRequested}
      />
      <PurgeDialog open={purgeOpen} onClose={() => setPurgeOpen(false)} onDone={() => { refreshUsers(); if (refresh) refresh(); }} />
    </div>
  );
}
